import React from 'react';
import { Box, Typography, Separator, formatCurrencyByCode } from '@lucid/lego';
import type { PricingSummary } from '@lucid/cart';
import styles from './SummaryPanel.module.scss';

type SummaryPanelLabels = {
  quantityTotal?: string;
  subtotalAllConfigurations?: string;
  totalOwedAllConfigurations?: string;
  availableCreditLine?: string;
  remainingAfterPurchase?: string;
};

export type SummaryPanelProps = {
  labels?: SummaryPanelLabels;
  pricing?: PricingSummary;
  currencyCode?: string;
  totalVehicles: number;
};

/**
 * Financial summary panel showing delivery location, pricing breakdown, and credit status.
 * @component
 * @param {SummaryPanelProps} props
 * @returns {JSX.Element}
 * @example
 * <SummaryPanel pricing={pricing} currencyCode="USD" totalVehicles={3} />
 */
const SummaryPanel: React.FC<SummaryPanelProps> = ({
  labels,
  pricing,
  currencyCode,
  totalVehicles,
}) => (
  <Box className={styles.summaryPanel} data-testid="checkout-summary-panel">
    <Box className={styles.summaryPanelLayout}>
      {/* Left — delivery location */}
      <Box className={styles.summaryLocation}>
        <Typography variant="body" size="extraSmall" color="gray-5" theme="dark">
          DELIVERY LOCATION*
        </Typography>
        <Typography variant="body" size="medium" color="white" theme="dark">
          Rotterdam
        </Typography>
      </Box>

      {/* Right — financial stats */}
      <Box className={styles.summaryStats}>
        <Box className={styles.summaryStatRow}>
          <Typography variant="body" size="medium" color="white" theme="dark">
            {labels?.quantityTotal ?? 'Quantity Total'}
          </Typography>
          <Typography variant="body" size="medium" color="white" theme="dark">
            {totalVehicles}
          </Typography>
        </Box>
        <Box className={styles.summaryStatRow}>
          <Typography variant="body" size="medium" color="white" theme="dark">
            {labels?.subtotalAllConfigurations ?? 'Subtotal All Configurations'}
          </Typography>
          <Typography variant="body" size="medium" color="white" theme="dark">
            {pricing
              ? formatCurrencyByCode(pricing.subtotal, currencyCode)
              : formatCurrencyByCode(0, currencyCode)}
          </Typography>
        </Box>
        <Box className={styles.summaryStatRow}>
          <Typography variant="body" size="medium" color="white" theme="dark">
            VAT 21%
          </Typography>
          <Typography variant="body" size="medium" color="white" theme="dark">
            {pricing
              ? formatCurrencyByCode(pricing.taxTotal, currencyCode)
              : formatCurrencyByCode(0, currencyCode)}
          </Typography>
        </Box>

        <Separator showLine />

        <Box className={styles.summaryTotalRow}>
          <Typography variant="subtitle" size="medium" color="white" theme="dark">
            {labels?.totalOwedAllConfigurations ?? 'Total Owed for All Configurations'}
          </Typography>
          <Typography variant="subtitle" size="medium" color="white" theme="dark">
            {pricing
              ? formatCurrencyByCode(pricing.grandTotal, currencyCode)
              : formatCurrencyByCode(0, currencyCode)}
          </Typography>
        </Box>

        <Separator showLine />

        <Box className={styles.summaryStatRow}>
          <Typography variant="body" size="small" color="white" theme="dark">
            {labels?.availableCreditLine ?? 'Available Credit Line'}
          </Typography>
          <Typography variant="body" size="small" color="white" theme="dark">
            {formatCurrencyByCode(12600000, currencyCode)}
          </Typography>
        </Box>
        <Box className={styles.summaryStatRow}>
          <Typography variant="body" size="small" color="white" theme="dark">
            {labels?.remainingAfterPurchase ?? 'Remaining after Purchase'}
          </Typography>
          <Typography variant="body" size="small" color="white" theme="dark">
            {formatCurrencyByCode(5577600, currencyCode)}
          </Typography>
        </Box>
      </Box>
    </Box>
  </Box>
);

export default SummaryPanel;
